import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { User, Mail, Phone, MapPin, Calendar, Briefcase, Lock, Save, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import * as api from '../../services/api';

const ProfilePage = ({ client, onClientUpdate }) => {
  const [password, setPassword] = useState(client.plainPassword || '');
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);

  // Format date from YYYY-MM-DD to DD/MM/YYYY
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const parts = dateString.split('-');
    if (parts.length === 3) {
      return `${parts[2]}/${parts[1]}/${parts[0]}`;
    }
    return dateString;
  };

  const handleSavePassword = async () => {
    if (!password.trim()) {
      toast.error('Le mot de passe ne peut pas être vide');
      return;
    }

    setSaving(true);
    try {
      await api.updateClientPassword(client.id, password);
      toast.success('Mot de passe mis à jour avec succès');
      if (onClientUpdate) {
        onClientUpdate();
      }
    } catch (error) {
      console.error('Error updating password:', error);
      toast.error('Erreur lors de la mise à jour du mot de passe');
    } finally {
      setSaving(false);
    }
  };

  const InfoRow = ({ icon: Icon, label, value }) => (
    <div className="flex items-start space-x-4 py-4 border-b border-gray-100 last:border-0">
      <div className="bg-green-100 p-3 rounded-lg">
        <Icon className="text-green-600" size={20} />
      </div>
      <div className="flex-1">
        <p className="text-sm text-gray-500">{label}</p>
        <p className="text-base font-medium text-gray-900 mt-1">{value || '-'}</p>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className="bg-gradient-to-r from-green-600 to-green-700 text-white border-0">
        <CardContent className="p-6">
          <div className="flex items-center space-x-4">
            <div className="bg-white/20 p-4 rounded-full">
              <User size={32} />
            </div>
            <div>
              <h2 className="text-2xl font-bold">{client.firstName} {client.lastName}</h2>
              <p className="text-green-100">Votre profil personnel</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Personal Information */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="text-green-600" size={20} />
            Informations Personnelles
          </CardTitle>
        </CardHeader>
        <CardContent>
          <InfoRow icon={User} label="Prénom" value={client.firstName} />
          <InfoRow icon={User} label="Nom" value={client.lastName} />
          <InfoRow icon={Mail} label="Email" value={client.email} />
          <InfoRow icon={Phone} label="Téléphone" value={client.phone} />
          <InfoRow icon={MapPin} label="Adresse" value={client.address} />
          <InfoRow icon={Calendar} label="Date de naissance" value={formatDate(client.dateOfBirth)} />
          <InfoRow icon={Briefcase} label="Profession" value={client.profession} />
        </CardContent>
      </Card>

      {/* Password Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Lock className="text-green-600" size={20} />
            Mot de passe
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Votre mot de passe actuel
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full border rounded-lg p-3 pr-12 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="Entrez votre nouveau mot de passe"
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              <p className="text-sm text-gray-500 mt-2">
                Vous pouvez modifier votre mot de passe ci-dessus et cliquer sur Enregistrer.
              </p>
            </div>

            <Button
              onClick={handleSavePassword}
              disabled={saving}
              className="w-full"
              style={{ backgroundColor: '#82C43C' }}
              data-testid="save-password-btn"
            >
              <Save size={16} className="mr-2" />
              {saving ? 'Enregistrement...' : 'Enregistrer le mot de passe'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Loan Information */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Briefcase className="text-green-600" size={20} />
            Informations du Prêt
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm text-gray-500">Montant du prêt</p>
              <p className="text-xl font-bold text-gray-900 mt-1">
                {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(client.loanAmount || 0)}
              </p>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm text-gray-500">Apport personnel</p>
              <p className="text-xl font-bold text-gray-900 mt-1">
                {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(client.contribution || 0)}
              </p>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm text-gray-500">Durée</p>
              <p className="text-xl font-bold text-gray-900 mt-1">{client.loanDuration || 0} ans</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm text-gray-500">Mensualité assurance comprise</p>
              <p className="text-xl font-bold text-gray-900 mt-1">
                {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(client.monthlyPayment || 0)}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ProfilePage;
